import { useRef, useState, type InputHTMLAttributes } from 'react';
import { File, Upload, X } from 'lucide-react';

import { cx } from '@/lib/cx';

import { Button } from './Button';
import buttonStyles from './Button.module.css';
import styles from './FilePicker.module.css';

type FilePickerProps = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'name' | 'required' | 'accept' | 'disabled' | 'aria-describedby' | 'aria-invalid'
> & {
  onPick?: (file: File) => void;
  fileName?: string;
  busy?: boolean;
};

export function FilePicker({ onPick, fileName, busy = false, ...inputProps }: FilePickerProps) {
  const input = useRef<HTMLInputElement>(null);
  const [selection, setSelection] = useState('');
  const name = fileName ?? selection;

  return (
    <div className={styles.panel}>
      <div className={styles.info} role="status">
        <span className={styles.icon}>
          {name ? (
            <File size={18} aria-hidden="true" />
          ) : (
            <Upload size={18} aria-hidden="true" />
          )}
        </span>
        <span className={styles.filename} title={name || undefined}>
          {busy ? 'Uploading…' : name || 'No file selected'}
        </span>
      </div>
      <div className={styles.actions}>
        <span
          className={cx(buttonStyles.btn, buttonStyles.ghost, buttonStyles.sm, styles.choose)}
        >
          <span aria-hidden="true">{name ? 'Replace file' : 'Choose file'}</span>
          <input
            {...inputProps}
            ref={input}
            className={styles.input}
            type="file"
            disabled={busy || inputProps.disabled}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (onPick) {
                event.target.value = '';
                if (file) onPick(file);
              } else {
                setSelection(file?.name ?? '');
              }
            }}
          />
        </span>
        {!onPick && selection ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Remove ${selection}`}
            icon={<X size={14} aria-hidden="true" />}
            disabled={inputProps.disabled}
            onClick={() => {
              if (input.current) input.current.value = '';
              setSelection('');
              input.current?.focus();
            }}
          >
            Remove
          </Button>
        ) : null}
      </div>
    </div>
  );
}
